/**
 * SOV Event Handler Pattern Generators
 *
 * Generates event handler patterns for Subject-Object-Verb languages:
 * Japanese, Korean, Turkish, Bengali, Quechua, Hindi.
 *
 * Extracted from pattern-generator.ts for maintainability.
 */

import type { LanguagePattern, PatternToken, ExtractionRule, SemanticRole } from '../types';
import type { LanguageProfile, KeywordTranslation, RoleMarker } from './language-profiles';
import type { CommandSchema, RoleSpec } from './command-schemas';
import {
  eventHandlerDestinationExtraction,
  eventHandlerDestinationGroup,
  eventHandlerPatientGroup,
  eventHandlerSourceExtraction,
  eventHandlerSourceGroup,
} from './command-schemas';
import { buildRoleToken, type GeneratorConfig } from './pattern-generator';
import { schemaMarkerAlternatives } from '../parser/utils/marker-resolution';

/**
 * Resolve the surface marker (and any allomorph alternatives) for a role in a
 * given language. Prefers the schema's per-command `markerOverride`, else the
 * profile's default roleMarker; in BOTH cases the schema's declared alternatives
 * (`markerLegacy` ∪ `markerVariants`) merge in via `schemaMarkerAlternatives`,
 * so the generated pattern matches every surface form the i18n transformer can
 * produce.
 *
 * Why: `markerOverride` is a single string, so it silently dropped the allomorph
 * alternatives the profile roleMarker would otherwise have supplied. The Turkish
 * dative the transformer emits under vowel harmony (e/a/ye/ya) is the canonical
 * case: tr set-attribute's `… doğru ya ayarla` (value=true marked with the -ya
 * dative) failed to match the `e`-only patient marker, so the whole set fell to
 * the role-scrambling generic SOV extraction. See STRUCTURAL_ARCS_ROADMAP.md.
 *
 * This used to be the only generator that merged variants in both branches,
 * which is why tr `set` worked here and nowhere else.
 */
function resolveRoleMarker(
  roleSpec: RoleSpec,
  profile: LanguageProfile
): { marker: string | undefined; alternatives: string[] | undefined } {
  let marker: string | undefined;
  let alternatives: string[] | undefined;

  if (roleSpec.markerOverride && roleSpec.markerOverride[profile.code] !== undefined) {
    marker = roleSpec.markerOverride[profile.code];
    // An override replaces the profile's alternatives, so the markers a previous
    // release rendered would stop parsing without markerLegacy.
    alternatives = marker ? schemaMarkerAlternatives(roleSpec, profile.code, marker) : undefined;
  } else {
    const roleMarker = profile.roleMarkers[roleSpec.role];
    if (roleMarker) {
      marker = roleMarker.primary;
      const schemaAlts = schemaMarkerAlternatives(roleSpec, profile.code, marker) ?? [];
      const merged = [...new Set([...(roleMarker.alternatives ?? []), ...schemaAlts])].filter(
        a => a !== marker
      );
      alternatives = merged.length ? merged : undefined;
    }
  }

  return { marker, alternatives };
}

/**
 * Append an optional trailing `[on {scope}]` group to a SOV event-handler
 * pattern when the schema declares a non-required `scope` role (S1 tabs-aria:
 * `set @attr to V on <scope>`). The i18n transformer appends `on <scope>` at the
 * clause end for SOV event-handler sets, so the scope is captured here. No-op for
 * commands without a scope role (e.g. put), keeping those patterns byte-identical.
 */
export function appendOptionalScope(
  tokens: PatternToken[],
  extraction: Record<string, ExtractionRule>,
  commandSchema: CommandSchema
): void {
  const scopeRole = commandSchema.roles.find(r => r.role === 'scope' && !r.required);
  if (!scopeRole) return;
  tokens.push({
    type: 'group',
    optional: true,
    tokens: [
      { type: 'literal', value: 'on' },
      { type: 'role', role: 'scope', optional: true, expectedTypes: ['selector', 'reference'] },
    ],
  });
  extraction.scope = { fromRole: 'scope' };
}

/**
 * Append an optional trailing `[using view {manner}]` group to an event-handler
 * pattern when the schema declares the view-transition `manner` role
 * (`swap … using view transition`, `process … using view transition`).
 *
 * The tail is a passthrough phrase — the i18n transformer masks it and
 * re-appends it verbatim at the CLAUSE END in every word order, because
 * `using view` has no native form in any of the 24 languages
 * (`USING_VIEW_MARKER_ALL_LANGS`). The generated command patterns get the slot
 * from the schema role, but the SOV/VSO event-handler patterns are hand-built
 * and had no slot, so all 8 of them (ar bn hi ja ko qu tl tr) dropped it as
 * `fused body walk left 3 token(s) unconsumed` while still matching the command
 * at confidence 1.0 — an R1 role loss invisible to every recall signal that
 * scores only the actions.
 *
 * Marker words come from the role's own `markerOverride` so the group cannot
 * drift from the schema. No-op for commands without the role, keeping every
 * other pattern byte-identical.
 */
export function appendOptionalViewTransition(
  tokens: PatternToken[],
  extraction: Record<string, ExtractionRule>,
  commandSchema: CommandSchema,
  languageCode: string
): void {
  appendOptionalTailRole(tokens, extraction, commandSchema, languageCode, 'manner', {
    requireMarker: true,
  });
}

/**
 * Append an optional trailing `[{recipient}]` group to an event-handler pattern
 * when the schema declares a non-required `recipient` role
 * (`take .active from .tab-button for me`).
 *
 * Marker-LESS in 23 of 24 languages by design (`takeSchema.recipient` overrides
 * only `en: 'for'`), because the i18n transformer renders the recipient as a
 * BARE trailing pronoun everywhere — `… von .tab-button ich`, `… .tab-button
 * থেকে আমি`. Safe as a marker-less trailing slot because the role is typed
 * `['reference']` and shape-anchored on `'reference'`: only `me`/`you`/`it`/
 * `result` can fill it, and the shape anchor keeps the uncaptured slot out of
 * `scoreRoleCoverage`'s denominator.
 *
 * Without it, bn/hi/ja/ko/tr matched take at confidence 1.0 and reported
 * `fused body walk left 1 token(s) unconsumed: "<pronoun>"` — five of the ten
 * rows in the baseline's `roleLossyPatterns` for `take-class-from-siblings`.
 * The other five were unrelated: it/ru/uk/vi had `get`'s hand-written pattern
 * claiming take's verb (`patterns/get.ts`), and qu never matches a pattern at
 * all.
 */
export function appendOptionalRecipient(
  tokens: PatternToken[],
  extraction: Record<string, ExtractionRule>,
  commandSchema: CommandSchema,
  languageCode: string
): void {
  appendOptionalTailRole(tokens, extraction, commandSchema, languageCode, 'recipient', {
    requireMarker: false,
  });
}

/**
 * Shared body of the two appenders above: emit `[<marker words> {role}]` as a
 * trailing optional group, with the marker (if any) and every type/shape knob
 * read from the schema's own `RoleSpec`, so a generated event-handler pattern
 * cannot drift from the command patterns' version of the same slot.
 *
 * `requireMarker` distinguishes the two cases: `manner` is reachable only
 * behind its required `using view` literals and must not emit a bare slot,
 * while `recipient` is marker-less in 23 languages and would never emit at all
 * under that rule.
 */
function appendOptionalTailRole(
  tokens: PatternToken[],
  extraction: Record<string, ExtractionRule>,
  commandSchema: CommandSchema,
  languageCode: string,
  role: SemanticRole,
  opts: { requireMarker: boolean }
): void {
  const spec = commandSchema.roles.find(r => r.role === role && !r.required);
  if (!spec) return;
  const marker = spec.markerOverride?.[languageCode];
  if (opts.requireMarker && !marker) return;

  tokens.push({
    type: 'group',
    optional: true,
    tokens: [
      ...(marker ? marker.split(/\s+/) : []).map((word): PatternToken => ({
        type: 'literal',
        value: word,
      })),
      {
        type: 'role',
        role,
        optional: true,
        expectedTypes: spec.expectedTypes,
        // Load-bearing, same as on the generated command patterns: it exempts
        // the trailing slot from the matcher's verb guard, keeps the uncaptured
        // slot out of `scoreRoleCoverage`'s denominator, and (for `keyword`)
        // normalizes the captured value type to the EN reference's `literal`.
        ...(spec.valueShape !== undefined ? { valueShape: spec.valueShape } : {}),
      },
    ],
  });
  extraction[role] = { fromRole: role };
}

/**
 * The role a fused event-handler pattern binds in its bare argument slot.
 *
 * `patient` for the commands that declare one; otherwise the schema's own
 * primary required role — `fetch`/`get` bind `source`, `tell` binds
 * `destination`, `repeat` binds `loopType`.
 *
 * Fourteen of the seventy schemas declare no `patient` role at all, and the
 * fused generators bound their argument to `patient` regardless: bn
 * `ক্লিক তে "/api/data" আনুন` produced a fetch whose URL sat in a role
 * `fetchSchema` does not have, leaving the required `source` unbound and the
 * profile's `from`-group free to swallow the NEXT phrase instead (pl's `z`
 * marks both `source` and `style`, so `z {method:"POST"}` bound as the source
 * and the style vanished).
 *
 * `event` is excluded because the fused pattern already owns an `event` slot
 * for the TRIGGER (`on click send foo`), so a wrapped command whose own primary
 * role is `event` (send/trigger/on) keeps the legacy `patient` binding.
 */
export function fusedBoundRole(commandSchema: CommandSchema): SemanticRole {
  if (commandSchema.roles.some(r => r.role === 'patient')) return 'patient';
  const required = commandSchema.roles.filter(r => r.required);
  const primary = required.find(r => r.role === commandSchema.primaryRole) ?? required[0];
  if (!primary || primary.role === 'event') return 'patient';
  return primary.role;
}

/**
 * Tokens for the fused pattern's required argument slot.
 *
 * `patient` is rendered bare by every fused surface, so it keeps the bare
 * token it always had. A schema whose primary role is `source`/`destination`
 * (fetch, get, tell, go, scroll) renders that role WITH its marker in some
 * languages and without it in others — pl `pobierz "/api/form"` versus ms
 * `dapatkan dari #input.value` — so the marker goes in an OPTIONAL group while
 * the role itself stays required. Requiring the marker cost `go`/`scroll`/
 * `tell` four languages each when it was tried; omitting it cost the same four.
 */
export function fusedBoundRoleTokens(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  role: SemanticRole
): PatternToken[] {
  const spec = commandSchema.roles.find(r => r.role === role);
  if (!spec || role === 'patient') return [{ type: 'role', role, optional: false }];

  const { marker, alternatives } = resolveRoleMarker(spec, profile);
  // Deliberately NO `expectedTypes`: the slot this replaces was an untyped
  // `{patient}`, and the defect being fixed is its NAME, not its permissiveness.
  // Narrowing it to the schema's declared types stopped `unless`'s condition
  // binding `I match .disabled` in pl/it/ru/uk/th, which is a matcher
  // type-inference gap rather than anything this function should adjudicate.
  const roleToken: PatternToken = {
    type: 'role',
    role,
    optional: false,
    ...(spec.valueShape !== undefined ? { valueShape: spec.valueShape } : {}),
  };
  if (!marker) return [roleToken];

  const markerWords = marker.split(/\s+/).filter(Boolean);
  const markerGroup: PatternToken = {
    type: 'group',
    optional: true,
    tokens: markerWords.map((word): PatternToken => ({
      type: 'literal',
      value: word,
      ...(markerWords.length === 1 && alternatives?.length ? { alternatives } : {}),
    })),
  };
  const position = profile.roleMarkers[role]?.position ?? 'before';
  return position === 'after' ? [roleToken, markerGroup] : [markerGroup, roleToken];
}

/**
 * Append a trailing optional slot for every optional role the fused pattern
 * does not already carry.
 *
 * The primary fused generators were hardcoded to `event + verb + patient +
 * [destination] + [source]` and never read `commandSchema.roles`, so NO
 * optional role could appear in any `<cmd>-event-{L}-*` pattern — and a fused
 * pattern outranks the standalone one (basePriority + 50) whenever its verb
 * matches. `fetch … with {style}` and `tell … to {destination}` therefore
 * round-tripped through the fused pattern with the role silently dropped, at
 * confidence 1.0, in every SOV/VSO language.
 *
 * `appendOptionalScope`/`appendOptionalViewTransition`/`appendOptionalRecipient`
 * above are the three one-role ancestors of this function, each added by an
 * arc that hit the same wall for a single role; they stay because they are
 * wired into the SECONDARY generators, which keep their hand-built shapes.
 *
 * Slots are built by the SAME `buildRoleToken` the standalone command patterns
 * use, so the fused slot and its standalone twin cannot drift — marker
 * resolution (`markerOverride → profile.roleMarkers`, plus `markerLegacy ∪
 * markerVariants` alternatives), `markerOptional`, `renderSuppress`,
 * `expectedTypes` and `valueShape` all come along.
 *
 * A role with NEITHER a marker in this language NOR a `valueShape` is skipped:
 * a bare, unanchored trailing slot has nothing to guard it and would capture
 * whatever follows. That excludes exactly `fetch.method`, `repeat.quantity` and
 * `repeat.event` — their surface markers live in handcrafted patterns rather
 * than in the schema or the profile, so giving them a fused slot is authoring
 * work, not generator work.
 */
export function appendRemainingOptionalRoles(
  tokens: PatternToken[],
  extraction: Record<string, ExtractionRule>,
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  alreadyEmitted: ReadonlySet<string>
): void {
  for (const spec of commandSchema.roles) {
    if (spec.required) continue;
    if (alreadyEmitted.has(spec.role)) continue;
    if (extraction[spec.role]) continue;
    const marker = spec.markerOverride?.[profile.code] ?? profile.roleMarkers[spec.role]?.primary;
    if (!marker && spec.valueShape === undefined) continue;
    tokens.push({ type: 'group', optional: true, tokens: buildRoleToken(spec, profile) });
    extraction[spec.role] = spec.default
      ? { fromRole: spec.role, default: spec.default }
      : { fromRole: spec.role };
  }
}

/**
 * Generate SOV event handler pattern (Japanese, Korean, Turkish).
 */
export function generateSOVEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  eventMarker: RoleMarker,
  config: GeneratorConfig
): LanguagePattern {
  const tokens: PatternToken[] = [];

  // Event role
  tokens.push({ type: 'role', role: 'event', optional: false });

  // Event marker (after event in SOV)
  // Handle multi-word markers like Korean "할 때" by splitting into separate tokens
  if (eventMarker.position === 'after') {
    const markerWords = eventMarker.primary.split(/\s+/);
    if (markerWords.length > 1) {
      // Multi-word marker: create a token for each word
      for (const word of markerWords) {
        tokens.push({ type: 'literal', value: word });
      }
    } else {
      // Single-word marker: include alternatives
      const markerToken: PatternToken = eventMarker.alternatives
        ? { type: 'literal', value: eventMarker.primary, alternatives: eventMarker.alternatives }
        : { type: 'literal', value: eventMarker.primary };
      tokens.push(markerToken);
    }
  }

  // The wrapped command's bare argument slot: `patient` for the commands that
  // declare one, otherwise the schema's own primary required role.
  const boundRole = fusedBoundRole(commandSchema);

  // Optional destination with its marker
  const destMarker = profile.roleMarkers.destination;
  // Gated on the SCHEMA, not on the profile alone: an unconditional destination
  // group fabricated a slot for commands that have no destination role, and the
  // marker then ate a phrase belonging to a role that does exist — ru
  // `увеличить #score на 10` bound `на 10` as increment's (nonexistent)
  // destination, so the quantity was dropped. Same contract as
  // eventHandlerDestinationGroup/eventHandlerSourceGroup, which have always
  // self-gated this way.
  const schemaHasDestination = commandSchema.roles.some(r => r.role === 'destination');
  if (destMarker && schemaHasDestination && boundRole !== 'destination') {
    tokens.push({
      type: 'group',
      optional: true,
      tokens: [
        { type: 'role', role: 'destination', optional: true },
        destMarker.alternatives
          ? { type: 'literal', value: destMarker.primary, alternatives: destMarker.alternatives }
          : { type: 'literal', value: destMarker.primary },
      ],
    });
  }

  // Optional SOURCE with its marker, the pre-verb twin of the destination group
  // above. Only the POST-verb source group existed, because that is where the
  // i18n transformer emits a from-phrase — but the semantic renderer emits it
  // BEFORE the patient (`আগের <li/> থেকে .highlight কে সরান`), a shape no fused
  // pattern covered. The untyped `{patient}` slot then swallowed the whole run
  // including the source marker, and `remove.source` was lost inside every
  // handler (bn previous-element). Gated on the SCHEMA exactly like the
  // destination group: an unconditional group fabricates a slot for commands
  // with no source role, and its marker then eats a phrase belonging to a role
  // that does exist.
  const srcMarkerPre = profile.roleMarkers.source;
  const schemaHasSource = commandSchema.roles.some(r => r.role === 'source');
  if (srcMarkerPre && schemaHasSource && boundRole !== 'source') {
    tokens.push({
      type: 'group',
      optional: true,
      tokens: [
        { type: 'role', role: 'source', optional: true },
        srcMarkerPre.alternatives
          ? {
              type: 'literal',
              value: srcMarkerPre.primary,
              alternatives: srcMarkerPre.alternatives,
            }
          : { type: 'literal', value: srcMarkerPre.primary },
      ],
    });
  }

  tokens.push(...fusedBoundRoleTokens(commandSchema, profile, boundRole));

  // Patient marker (postposition/particle after patient)
  const patientMarker = profile.roleMarkers.patient;
  if (patientMarker && boundRole === 'patient') {
    const patMarkerToken: PatternToken = patientMarker.alternatives
      ? { type: 'literal', value: patientMarker.primary, alternatives: patientMarker.alternatives }
      : { type: 'literal', value: patientMarker.primary };
    tokens.push(patMarkerToken);
  }

  // Command verb at end (SOV)
  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };
  tokens.push(verbToken);

  // Optional trailing source phrase (post-verb, where the transformer
  // emits `remove X from Y`'s from-phrase in SOV output)
  if (boundRole !== 'source') {
    tokens.push(...eventHandlerSourceGroup(commandSchema, profile.roleMarkers.source));
  }
  if (boundRole !== 'destination') {
    tokens.push(...eventHandlerDestinationGroup(commandSchema, profile.roleMarkers.destination));
  }

  const extraction: Record<string, ExtractionRule> = {
    action: { value: commandSchema.action },
    event: { fromRole: 'event' },
    [boundRole]: { fromRole: boundRole },
    ...(boundRole === 'destination' ? {} : eventHandlerDestinationExtraction(commandSchema)),
    ...(boundRole === 'source' ? {} : eventHandlerSourceExtraction(commandSchema)),
  };
  appendRemainingOptionalRoles(
    tokens,
    extraction,
    commandSchema,
    profile,
    new Set(['event', boundRole, 'destination', 'source'])
  );

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov`,
    language: profile.code,
    command: 'on', // This is an event handler pattern
    priority: (config.basePriority ?? 100) + 50, // Higher priority than simple commands
    template: {
      format: `{event} ${eventMarker.primary} {destination?} {${boundRole}} ${patientMarker?.primary || ''} ${keyword.primary}`,
      tokens,
    },
    extraction,
  };
}

/**
 * Generate patient-first SOV event handler pattern (simple, no destination).
 *
 * Alternative SOV ordering where the patient precedes the event:
 * - Bengali: .active কে ক্লিক এ টগল  (patient কে event এ verb)
 * - Quechua: .active ta ñitiy pi tikray  (patient ta event pi verb)
 *
 * No optional destination group to avoid greedy match issues when
 * destination and event markers collide (e.g., Bengali তে/এ).
 */
export function generateSOVPatientFirstEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  eventMarker: RoleMarker,
  config: GeneratorConfig
): LanguagePattern {
  const tokens: PatternToken[] = [];

  // `swap` is the two-object exception, and its operands are NOT symmetric:
  // `swap #a with #b` gives #a the content of #b, so the FRONTED (accusative)
  // element is the target — `destination` in en's reading and in swapSchema's
  // own role descriptions — and the trailing with-marked element is the
  // content (`patient`). Binding the fronted slot to `patient` here is what
  // made ar/bn/hi/ja/ko/qu/tl/tr swap the wrong way round once #845 made the
  // command execute at all (R3 family 6, re-opened 2026-07-31 with the
  // falsifying measurement — the old "runtime-symmetric" premise was vacuously
  // true only while swap threw for every language, English included).
  //
  // The accusative MARKER is unchanged; only the role it fills is. Fixing the
  // eight rather than renaming en's roles keeps `destination`/`patient`
  // meaning what the schema says they mean.
  const swapsOperands = commandSchema.action === 'swap';
  const frontedRole: SemanticRole = swapsOperands ? 'destination' : 'patient';

  // Fronted role first
  tokens.push({ type: 'role', role: frontedRole, optional: false });

  // Patient marker (postposition after the fronted element)
  const patientMarker = profile.roleMarkers.patient;
  if (patientMarker) {
    const patMarkerToken: PatternToken = patientMarker.alternatives
      ? { type: 'literal', value: patientMarker.primary, alternatives: patientMarker.alternatives }
      : { type: 'literal', value: patientMarker.primary };
    tokens.push(patMarkerToken);
  }

  // Event role
  tokens.push({ type: 'role', role: 'event', optional: false });

  // Event marker (after event in SOV)
  if (eventMarker.position === 'after') {
    const markerWords = eventMarker.primary.split(/\s+/);
    if (markerWords.length > 1) {
      for (const word of markerWords) {
        tokens.push({ type: 'literal', value: word });
      }
    } else {
      const markerToken: PatternToken = eventMarker.alternatives
        ? { type: 'literal', value: eventMarker.primary, alternatives: eventMarker.alternatives }
        : { type: 'literal', value: eventMarker.primary };
      tokens.push(markerToken);
    }
  }

  // Command verb at end (SOV)
  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };
  tokens.push(verbToken);

  // Optional trailing source phrase (post-verb, where the transformer
  // emits `remove X from Y`'s from-phrase in SOV output)
  tokens.push(...eventHandlerSourceGroup(commandSchema, profile.roleMarkers.source));
  // Trailing destination phrase. `swap` is the two-object exception: its second
  // element is the WITH-marked patient content (`#b से` / `#b দিয়ে`), but the
  // patient-first flip binds the first element to `patient`, so the trailing
  // element must bind to `destination` — and the group therefore has to accept
  // the language's with-word, not only the locative dest-marker. Merge the
  // patient's with-word (schema markerOverride) into the dest-marker's
  // alternatives for swap; otherwise `#b <with>` never matches and #b drops
  // (hi/bn/tr/qu rendered the invalid `swap with #a`). Non-swap commands and
  // languages without a with-override are byte-identical to before.
  let trailingMarker = profile.roleMarkers.destination;
  if (swapsOperands && trailingMarker) {
    const withWord = commandSchema.roles.find(r => r.role === 'patient')?.markerOverride?.[
      profile.code
    ];
    if (withWord && withWord !== trailingMarker.primary) {
      const existing = trailingMarker.alternatives ?? [];
      if (!existing.includes(withWord)) {
        trailingMarker = { ...trailingMarker, alternatives: [...existing, withWord] };
      }
    }
  }
  tokens.push(
    ...(swapsOperands
      ? eventHandlerPatientGroup(commandSchema, trailingMarker)
      : eventHandlerDestinationGroup(commandSchema, trailingMarker))
  );

  const extraction: Record<string, ExtractionRule> = {
    action: { value: commandSchema.action },
    event: { fromRole: 'event' },
    [frontedRole]: { fromRole: frontedRole },
    ...(swapsOperands ? {} : eventHandlerDestinationExtraction(commandSchema)),
    ...eventHandlerSourceExtraction(commandSchema),
  };
  // take's bare trailing pronoun, then `using view transition`, both at the very
  // end — after the with-marked operand and after the source phrase, which is
  // where the i18n transformer puts them in verb-final output.
  appendOptionalRecipient(tokens, extraction, commandSchema, profile.code);
  appendOptionalViewTransition(tokens, extraction, commandSchema, profile.code);

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov-patient-first`,
    language: profile.code,
    command: 'on',
    priority: (config.basePriority ?? 100) + 45,
    template: {
      format: `{patient} ${patientMarker?.primary || ''} {event} ${eventMarker.primary} ${keyword.primary}`,
      tokens,
    },
    extraction,
  };
}

/**
 * Source-fronted patient-first SOV variant: the SOURCE phrase sits between the
 * patient and the event, pre-verb —
 *
 *   [patient] [patMarker] [source] [srcMarker] [event] [eventMarker] [verb] [recipient?] [manner?]
 *
 * qu's canonical order fronts the source phrase for source-carrying commands:
 * take `.active ta .tab-button manta ñitiy pi hapiy noqa`, remove
 * `.open ta noqa manta ñitiy pi qichuy`. No other generated pattern covers the
 * shape, so qu matched nothing and the verb-anchoring fallback bound the
 * trailing pronoun to `destination` (#874's deferred third cause).
 *
 * The source slot is REQUIRED, marker and all: optional, this pattern is the
 * plain patient-first pattern plus greedy-match ambiguity (the trap the
 * WithDest variant documents); required, it competes only when a marked source
 * phrase truly sits between patient and event. Not generated for `swap`
 * (no source role, and its fronted slot binds `destination`).
 */
export function generateSOVPatientFirstSourceFrontedEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  eventMarker: RoleMarker,
  config: GeneratorConfig
): LanguagePattern | null {
  const sourceSpec = commandSchema.roles.find(r => r.role === 'source');
  if (!sourceSpec) return null;
  const { marker: sourceMarker, alternatives: sourceAlts } = resolveRoleMarker(sourceSpec, profile);
  const patientMarker = profile.roleMarkers.patient;
  // A marked source slot is the variant's whole anchor — without a marker, or
  // with one colliding with the event/patient markers, the shape is ambiguous.
  if (!sourceMarker) return null;
  if (sourceMarker === eventMarker.primary || sourceMarker === patientMarker?.primary) return null;

  const tokens: PatternToken[] = [];

  tokens.push({ type: 'role', role: 'patient', optional: false });
  if (patientMarker) {
    tokens.push(
      patientMarker.alternatives
        ? {
            type: 'literal',
            value: patientMarker.primary,
            alternatives: patientMarker.alternatives,
          }
        : { type: 'literal', value: patientMarker.primary }
    );
  }

  tokens.push({
    type: 'role',
    role: 'source',
    optional: false,
    ...(sourceSpec.expectedTypes ? { expectedTypes: sourceSpec.expectedTypes } : {}),
    ...(sourceSpec.valueShape !== undefined ? { valueShape: sourceSpec.valueShape } : {}),
  });
  for (const word of sourceMarker.split(/\s+/)) {
    tokens.push(
      sourceAlts && sourceMarker.split(/\s+/).length === 1
        ? { type: 'literal', value: word, alternatives: sourceAlts }
        : { type: 'literal', value: word }
    );
  }

  tokens.push({ type: 'role', role: 'event', optional: false });
  if (eventMarker.position === 'after') {
    const markerWords = eventMarker.primary.split(/\s+/);
    if (markerWords.length > 1) {
      for (const word of markerWords) tokens.push({ type: 'literal', value: word });
    } else {
      tokens.push(
        eventMarker.alternatives
          ? { type: 'literal', value: eventMarker.primary, alternatives: eventMarker.alternatives }
          : { type: 'literal', value: eventMarker.primary }
      );
    }
  }

  tokens.push(
    keyword.alternatives
      ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
      : { type: 'literal', value: keyword.primary }
  );

  const extraction: Record<string, ExtractionRule> = {
    action: { value: commandSchema.action },
    event: { fromRole: 'event' },
    patient: { fromRole: 'patient' },
    source: { fromRole: 'source' },
  };
  appendOptionalRecipient(tokens, extraction, commandSchema, profile.code);
  appendOptionalViewTransition(tokens, extraction, commandSchema, profile.code);

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov-source-fronted`,
    language: profile.code,
    command: 'on',
    priority: (config.basePriority ?? 100) + 48,
    template: {
      format: `{patient} ${patientMarker?.primary || ''} {source} ${sourceMarker} {event} ${eventMarker.primary} ${keyword.primary}`,
      tokens,
    },
    extraction,
  };
}

/**
 * Generate patient-first SOV event handler pattern with required destination.
 *
 * For patterns like:
 * - Quechua: .highlight ta noqa man ñitiy pi yapay  (patient ta dest man event pi verb)
 *
 * Destination is REQUIRED (not optional) to avoid greedy match ambiguity.
 * Only generated when destination and event markers are different.
 */
export function generateSOVPatientFirstWithDestEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  eventMarker: RoleMarker,
  config: GeneratorConfig
): LanguagePattern {
  const tokens: PatternToken[] = [];

  // Patient role first
  tokens.push({ type: 'role', role: 'patient', optional: false });

  // Patient marker
  const patientMarker = profile.roleMarkers.patient;
  if (patientMarker) {
    const patMarkerToken: PatternToken = patientMarker.alternatives
      ? { type: 'literal', value: patientMarker.primary, alternatives: patientMarker.alternatives }
      : { type: 'literal', value: patientMarker.primary };
    tokens.push(patMarkerToken);
  }

  // Required destination with its marker
  const destMarker = profile.roleMarkers.destination;
  if (destMarker) {
    tokens.push({ type: 'role', role: 'destination', optional: false });
    tokens.push(
      destMarker.alternatives
        ? { type: 'literal', value: destMarker.primary, alternatives: destMarker.alternatives }
        : { type: 'literal', value: destMarker.primary }
    );
  }

  // Event role
  tokens.push({ type: 'role', role: 'event', optional: false });

  // Event marker (after event in SOV)
  if (eventMarker.position === 'after') {
    const markerWords = eventMarker.primary.split(/\s+/);
    if (markerWords.length > 1) {
      for (const word of markerWords) {
        tokens.push({ type: 'literal', value: word });
      }
    } else {
      const markerToken: PatternToken = eventMarker.alternatives
        ? { type: 'literal', value: eventMarker.primary, alternatives: eventMarker.alternatives }
        : { type: 'literal', value: eventMarker.primary };
      tokens.push(markerToken);
    }
  }

  // Command verb at end (SOV)
  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };
  tokens.push(verbToken);

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov-patient-first-dest`,
    language: profile.code,
    command: 'on',
    priority: (config.basePriority ?? 100) + 40, // Lower than simple patient-first
    template: {
      format: `{patient} ${patientMarker?.primary || ''} {destination} ${destMarker?.primary || ''} {event} ${eventMarker.primary} ${keyword.primary}`,
      tokens,
    },
    extraction: {
      action: { value: commandSchema.action },
      event: { fromRole: 'event' },
      patient: { fromRole: 'patient' },
      destination: { fromRole: 'destination' },
    },
  };
}

/**
 * Generate SOV compact event handler pattern for languages with no-space forms.
 *
 * This handles Korean compact forms where the event marker is attached directly
 * to the event word without a space:
 * - 클릭할때 .active를토글 (click+when toggle .active)
 *
 * The pattern uses a single token for the no-space marker alternatives.
 */
export function generateSOVCompactEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  eventMarker: RoleMarker,
  config: GeneratorConfig
): LanguagePattern {
  const tokens: PatternToken[] = [];

  // Event role
  tokens.push({ type: 'role', role: 'event', optional: false });

  // Event marker as single token (using no-space alternatives)
  // Filter alternatives to only include no-space versions
  const noSpaceAlternatives =
    eventMarker.alternatives?.filter(alt => !alt.includes(' ') && alt.length > 1) || [];

  if (noSpaceAlternatives.length > 0) {
    tokens.push({
      type: 'literal',
      value: noSpaceAlternatives[0],
      alternatives: noSpaceAlternatives.slice(1),
    });
  }

  // Optional destination with its marker
  const destMarker = profile.roleMarkers.destination;
  if (destMarker) {
    tokens.push({
      type: 'group',
      optional: true,
      tokens: [
        { type: 'role', role: 'destination', optional: true },
        destMarker.alternatives
          ? { type: 'literal', value: destMarker.primary, alternatives: destMarker.alternatives }
          : { type: 'literal', value: destMarker.primary },
      ],
    });
  }

  // Patient role
  tokens.push({ type: 'role', role: 'patient', optional: false });

  // Patient marker (postposition/particle after patient)
  const patientMarker = profile.roleMarkers.patient;
  if (patientMarker) {
    const patMarkerToken: PatternToken = patientMarker.alternatives
      ? { type: 'literal', value: patientMarker.primary, alternatives: patientMarker.alternatives }
      : { type: 'literal', value: patientMarker.primary };
    tokens.push(patMarkerToken);
  }

  // Command verb at end (SOV)
  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };
  tokens.push(verbToken);

  // Optional trailing source phrase (post-verb, where the transformer
  // emits `remove X from Y`'s from-phrase in SOV output)
  tokens.push(...eventHandlerSourceGroup(commandSchema, profile.roleMarkers.source));
  tokens.push(...eventHandlerDestinationGroup(commandSchema, profile.roleMarkers.destination));

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov-compact`,
    language: profile.code,
    command: 'on', // This is an event handler pattern
    priority: (config.basePriority ?? 100) + 52, // Slightly higher priority for compact forms
    template: {
      format: `{event}${noSpaceAlternatives[0] || ''} {patient} ${keyword.primary}`,
      tokens,
    },
    extraction: {
      action: { value: commandSchema.action }, // Extract the wrapped command
      event: { fromRole: 'event' },
      patient: { fromRole: 'patient' },
      ...eventHandlerDestinationExtraction(commandSchema),
      ...eventHandlerSourceExtraction(commandSchema),
    },
  };
}

/**
 * Generate SOV simple event handler pattern (patient optional, defaults to 'me').
 *
 * Supports patterns like:
 * - Japanese: クリック で 増加 (click on increment)
 * - Korean: 클릭 할 때 증가 (click when increment)
 * - Turkish: tıklama da artır (click on increment)
 *
 * The patient is not explicitly specified - it defaults to 'me' (current element).
 */
export function generateSOVSimpleEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  eventMarker: RoleMarker,
  config: GeneratorConfig
): LanguagePattern {
  const tokens: PatternToken[] = [];

  // Event role
  tokens.push({ type: 'role', role: 'event', optional: false });

  // Event marker (after event in SOV)
  if (eventMarker.position === 'after') {
    const markerWords = eventMarker.primary.split(/\s+/);
    if (markerWords.length > 1) {
      // Multi-word marker: create a token for each word
      for (const word of markerWords) {
        tokens.push({ type: 'literal', value: word });
      }
    } else {
      const markerToken: PatternToken = eventMarker.alternatives
        ? { type: 'literal', value: eventMarker.primary, alternatives: eventMarker.alternatives }
        : { type: 'literal', value: eventMarker.primary };
      tokens.push(markerToken);
    }
  }

  // Command verb at end (SOV) - no patient required
  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };
  tokens.push(verbToken);

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov-simple`,
    language: profile.code,
    command: 'on',
    priority: (config.basePriority ?? 100) + 48, // Lower than full pattern (50) but higher than base
    template: {
      format: `{event} ${eventMarker.primary} ${keyword.primary}`,
      tokens,
    },
    extraction: {
      action: { value: commandSchema.action },
      event: { fromRole: 'event' },
      patient: { default: { type: 'reference', value: 'me' } }, // Default to 'me'
    },
  };
}

/**
 * Generate SOV temporal event handler pattern.
 *
 * Supports patterns with temporal markers like:
 * - Japanese: クリック 時 .active を 切り替え (click time toggle .active)
 * - Japanese: クリック の 時 .active を 切り替え (click's time toggle .active)
 *
 * Uses profile.eventHandler.temporalMarkers for language-specific temporal words.
 */
export function generateSOVTemporalEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  config: GeneratorConfig
): LanguagePattern | null {
  const temporalMarkers = profile.eventHandler?.temporalMarkers;
  if (!temporalMarkers || temporalMarkers.length === 0) return null;

  const tokens: PatternToken[] = [];

  // Event role
  tokens.push({ type: 'role', role: 'event', optional: false });

  // Optional possessive marker (の in Japanese)
  if (profile.possessive?.marker) {
    tokens.push({
      type: 'group',
      optional: true,
      tokens: [{ type: 'literal', value: profile.possessive.marker }],
    });
  }

  // Temporal marker (時, とき in Japanese)
  tokens.push({
    type: 'literal',
    value: temporalMarkers[0],
    alternatives: temporalMarkers.slice(1),
  });

  // Patient role
  tokens.push({ type: 'role', role: 'patient', optional: false });

  // Patient marker
  const patientMarker = profile.roleMarkers.patient;
  if (patientMarker?.primary) {
    const patMarkerToken: PatternToken = patientMarker.alternatives
      ? { type: 'literal', value: patientMarker.primary, alternatives: patientMarker.alternatives }
      : { type: 'literal', value: patientMarker.primary };
    tokens.push(patMarkerToken);
  }

  // Command verb at end
  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };
  tokens.push(verbToken);

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov-temporal`,
    language: profile.code,
    command: 'on',
    priority: (config.basePriority ?? 100) + 49, // Between simple and full pattern
    template: {
      format: `{event} ${temporalMarkers[0]} {patient} ${keyword.primary}`,
      tokens,
    },
    extraction: {
      action: { value: commandSchema.action },
      event: { fromRole: 'event' },
      patient: { fromRole: 'patient' },
    },
  };
}
/**
 * Generate SOV two-role event handler pattern (for put/set commands).
 *
 * Patterns:
 * - Japanese put: 入力 で "test" を #output に 入れる
 *   [event] [eventMarker] [patient] [patientMarker] [destination] [destMarker] [verb]
 * - Korean set: 변경 할 때 x 를 10 에 설정
 *   [event] [eventMarker] [role1] [role1Marker] [role2] [role2Marker] [verb]
 * - Turkish put: giriş de "test" i #output a koy
 *   [event] [eventMarker] [patient] [patientMarker] [destination] [destMarker] [verb]
 */
export function generateSOVTwoRoleEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  eventMarker: RoleMarker,
  config: GeneratorConfig
): LanguagePattern {
  const tokens: PatternToken[] = [];

  // Event role
  tokens.push({ type: 'role', role: 'event', optional: false });

  // Event marker (after event in SOV)
  // Handle multi-word markers like Korean "할 때" by splitting into separate tokens
  if (eventMarker.position === 'after') {
    const markerWords = eventMarker.primary.split(/\s+/);
    if (markerWords.length > 1) {
      // Multi-word marker: create a token for each word
      for (const word of markerWords) {
        tokens.push({ type: 'literal', value: word });
      }
    } else {
      // Single-word marker: include alternatives
      const markerToken: PatternToken = eventMarker.alternatives
        ? { type: 'literal', value: eventMarker.primary, alternatives: eventMarker.alternatives }
        : { type: 'literal', value: eventMarker.primary };
      tokens.push(markerToken);
    }
  }

  // Get the two required roles from the schema
  const requiredRoles = commandSchema.roles.filter(r => r.required);

  // Sort by SOV position (lower number = earlier in sentence)
  const sortedRoles = [...requiredRoles].sort((a, b) => {
    const aPos = a.sovPosition ?? 999;
    const bPos = b.sovPosition ?? 999;
    return aPos - bPos;
  });

  // Add each role with its marker
  for (const roleSpec of sortedRoles) {
    // Add the role
    tokens.push({ type: 'role', role: roleSpec.role, optional: false });

    // Get marker for this role - markerOverride/profile default + markerVariants.
    const { marker, alternatives: markerAlternatives } = resolveRoleMarker(roleSpec, profile);

    // Add the marker token
    if (marker) {
      const markerToken: PatternToken = markerAlternatives
        ? { type: 'literal', value: marker, alternatives: markerAlternatives }
        : { type: 'literal', value: marker };
      tokens.push(markerToken);
    }
  }

  // Command verb at end (SOV)
  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };
  tokens.push(verbToken);

  // Build format string
  const roleNames = sortedRoles.map(r => `{${r.role}}`).join(' ');

  const extraction: Record<string, ExtractionRule> = {
    action: { value: commandSchema.action }, // Extract the wrapped command
    event: { fromRole: 'event' },
    ...Object.fromEntries(sortedRoles.map(r => [r.role, { fromRole: r.role }])),
  };
  appendOptionalScope(tokens, extraction, commandSchema);

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov-2role`,
    language: profile.code,
    command: 'on', // This is an event handler pattern
    priority: (config.basePriority ?? 100) + 55, // Higher priority than single-role patterns
    template: {
      format: `{event} ${eventMarker.primary} ${roleNames} ${keyword.primary}`,
      tokens,
    },
    extraction,
  };
}

/**
 * Generate event-LAST SOV two-role event handler pattern.
 *
 * Some SOV emissions place the whole event phrase AFTER the command verb, as a
 * trailing afterthought:
 * - Japanese put: "Done!" を 私 に 置く クリック で
 *   [patient] [pMarker] [destination] [dMarker] [verb] [event] [eventMarker]
 *
 * The event-first (`{event} で {p} {d} 置く`) and dest-first
 * (`{p} {event} で 置く {d}`) variants both expect the event before the verb, so
 * this order fell to the bare command pattern (`put-ja-generated`) — which runs
 * immediately at execute() time, before the click is dispatched, so the effect
 * is invisible to the R2 execution snapshot (S4, ja put-content-basic).
 */
export function generateSOVTwoRoleEventLastEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  eventMarker: RoleMarker,
  config: GeneratorConfig
): LanguagePattern {
  const tokens: PatternToken[] = [];

  const requiredRoles = commandSchema.roles.filter(r => r.required);
  const sortedRoles = [...requiredRoles].sort((a, b) => {
    const aPos = a.sovPosition ?? 999;
    const bPos = b.sovPosition ?? 999;
    return aPos - bPos;
  });

  // Roles + their markers first (SOV position order)
  for (const roleSpec of sortedRoles) {
    tokens.push({ type: 'role', role: roleSpec.role, optional: false });
    const { marker, alternatives: markerAlternatives } = resolveRoleMarker(roleSpec, profile);
    if (marker) {
      tokens.push(
        markerAlternatives
          ? { type: 'literal', value: marker, alternatives: markerAlternatives }
          : { type: 'literal', value: marker }
      );
    }
  }

  // Command verb (SOV: after the roles)
  tokens.push(
    keyword.alternatives
      ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
      : { type: 'literal', value: keyword.primary }
  );

  // Trailing event phrase: {event} {eventMarker}
  tokens.push({ type: 'role', role: 'event', optional: false });
  if (eventMarker.position === 'after') {
    const markerWords = eventMarker.primary.split(/\s+/);
    if (markerWords.length > 1) {
      for (const word of markerWords) tokens.push({ type: 'literal', value: word });
    } else {
      tokens.push(
        eventMarker.alternatives
          ? { type: 'literal', value: eventMarker.primary, alternatives: eventMarker.alternatives }
          : { type: 'literal', value: eventMarker.primary }
      );
    }
  }

  const roleNames = sortedRoles.map(r => `{${r.role}}`).join(' ');

  const extraction: Record<string, ExtractionRule> = {
    action: { value: commandSchema.action },
    event: { fromRole: 'event' },
    ...Object.fromEntries(sortedRoles.map(r => [r.role, { fromRole: r.role }])),
  };
  appendOptionalScope(tokens, extraction, commandSchema);

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov-2role-event-last`,
    language: profile.code,
    command: 'on',
    // Below the event-first / dest-first variants — this is the fallback order.
    priority: (config.basePriority ?? 100) + 40,
    template: {
      format: `${roleNames} ${keyword.primary} {event} ${eventMarker.primary}`,
      tokens,
    },
    extraction,
  };
}

/**
 * Generate destination-first SOV two-role event handler pattern.
 *
 * For languages where the roles precede the event in SOV order:
 * - Bengali: @disabled কে ক্লিক এ সেট সত্য তে
 *   [destination] [destMarker] [event] [eventMarker] [verb] [patient] [patientMarker]
 * - Bengali: "Hello" কে ক্লিক এ রাখুন #output তে
 *   [patient] [patientMarker] [event] [eventMarker] [verb] [destination] [destMarker]
 */
export function generateSOVTwoRoleDestFirstEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  eventMarker: RoleMarker,
  config: GeneratorConfig
): LanguagePattern {
  const tokens: PatternToken[] = [];

  // Get the two required roles from the schema
  const requiredRoles = commandSchema.roles.filter(r => r.required);
  const sortedRoles = [...requiredRoles].sort((a, b) => {
    const aPos = a.sovPosition ?? 999;
    const bPos = b.sovPosition ?? 999;
    return aPos - bPos;
  });

  // First role before event
  const firstRole = sortedRoles[0];
  tokens.push({ type: 'role', role: firstRole.role, optional: false });

  // First role marker (markerOverride/profile default + markerVariants allomorphs)
  const { marker: firstMarker, alternatives: firstMarkerAlts } = resolveRoleMarker(
    firstRole,
    profile
  );
  if (firstMarker) {
    const firstMarkerToken: PatternToken = firstMarkerAlts
      ? { type: 'literal', value: firstMarker, alternatives: firstMarkerAlts }
      : { type: 'literal', value: firstMarker };
    tokens.push(firstMarkerToken);
  }

  // Event role
  tokens.push({ type: 'role', role: 'event', optional: false });

  // Event marker
  if (eventMarker.position === 'after') {
    const markerWords = eventMarker.primary.split(/\s+/);
    if (markerWords.length > 1) {
      for (const word of markerWords) {
        tokens.push({ type: 'literal', value: word });
      }
    } else {
      const markerToken: PatternToken = eventMarker.alternatives
        ? { type: 'literal', value: eventMarker.primary, alternatives: eventMarker.alternatives }
        : { type: 'literal', value: eventMarker.primary };
      tokens.push(markerToken);
    }
  }

  // Command verb (between event and second role in this variant)
  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };
  tokens.push(verbToken);

  // Second role after verb
  const secondRole = sortedRoles[1];
  tokens.push({ type: 'role', role: secondRole.role, optional: false });

  // Second role marker (markerOverride/profile default + markerVariants allomorphs)
  const { marker: secondMarker, alternatives: secondMarkerAlts } = resolveRoleMarker(
    secondRole,
    profile
  );
  if (secondMarker) {
    const markerToken: PatternToken = secondMarkerAlts
      ? { type: 'literal', value: secondMarker, alternatives: secondMarkerAlts }
      : { type: 'literal', value: secondMarker };
    tokens.push(markerToken);
  }

  const extraction: Record<string, ExtractionRule> = {
    action: { value: commandSchema.action },
    event: { fromRole: 'event' },
    [firstRole.role]: { fromRole: firstRole.role },
    [secondRole.role]: { fromRole: secondRole.role },
  };
  appendOptionalScope(tokens, extraction, commandSchema);

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov-2role-dest-first`,
    language: profile.code,
    command: 'on',
    priority: (config.basePriority ?? 100) + 48, // Slightly lower than event-first two-role
    template: {
      format: `{${firstRole.role}} {event} ${eventMarker.primary} ${keyword.primary} {${secondRole.role}}`,
      tokens,
    },
    extraction,
  };
}
